<script setup lang="ts">
import importsBar from '@/charts/country/imports-bar'
import importsSankey from '@/charts/country/imports-sankey'
import type { Values } from '@/composables/useValues'

const props = defineProps<{
    dimension: Dimension
    country: Country
    data: Values<Import>
}>()

/**
 * Translations.
 */
const { t } = useI18n()

/**
 * Container of the bar chart.
 */
const barContainer = ref<HTMLElement>()

/**
 * Instance of the bar chart.
 */
const { instance: importsBarInstance } = useChart(barContainer, () => {
    return importsBar(
        props.data.countryData,
        props.data.formatter,
        `${props.country.name}: ${t('at-risk-imports')}`
    )
})

/**
 * Container of the sankey chart.
 */
const sankeyContainer = ref<HTMLElement>()

/**
 * Instance of the sankey chart.
 */
const { instance: importsSankeyInstance } = useChart(sankeyContainer, () => {
    return importsSankey(
        props.data?.data,
        props.data.countryData,
        props.data.formatter,
        t('overall-at-risk-imports'),
        `${props.country.name}: ${t('imports-share')}`
    )
})
</script>

<template>
    <CountryCard
        :title="t('top-risk-products')"
        :loading="data.loading"
        :dimension="dimension"
        :country="country"
        class="col-span-2 row-span-2 !aspect-auto screen-ratio-[2]:row-span-4 xl:col-start-5"
        condensed="screen-ratio-[2]:row-span-4 screen-ratio-[2]:!col-start-7 col-span-2 !aspect-auto xl:col-start-5 row-span-2"
    >
        <CountryImportingRiskProductsList :data="data" class="py-2" />
    </CountryCard>

    <CountryCard
        v-slot="{ condensed }"
        :title="t('at-risk-imports')"
        :loading="data.loading"
        :dimension="dimension"
        expanded-if-null
        :country="country"
        :instance="importsBarInstance"
        expanded="screen-ratio-[2]:row-span-4 col-span-4 row-span-4"
        class="col-span-4 row-span-4 screen-ratio-[2]:row-span-4"
        condensed="screen-ratio-[2]:row-span-4 col-span-2 col-start-5 row-span-2"
    >
        <CountryImportingRiskTotalValue
            :data="data"
            :country="country"
            :condensed="condensed"
            class="py-2"
        />
        <div class="flex-1 overflow-hidden">
            <div ref="barContainer" class="h-full" />
        </div>
    </CountryCard>

    <CountryCard
        id="sankey"
        :title="t('imports-share')"
        :loading="data.loading"
        :dimension="dimension"
        :instance="importsSankeyInstance"
        :country="country"
        condensed="screen-ratio-[2]:row-span-4 col-span-2 row-span-2"
        class="col-span-2 row-span-2 screen-ratio-[2]:row-span-4"
        expanded="screen-ratio-[2]:row-span-4 col-span-4 row-span-4"
    >
        <div ref="sankeyContainer" class="h-full" />
    </CountryCard>
</template>